import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button } from "@mui/joy";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import {
  AdminEvent,
  fetchAdminEvent,
} from "../api";
import AdminEventParticipants, {
  AdminEventParticipantsHandle,
} from "../components/Admin/AdminEventParticipants";
import PageHero from "../components/common/PageHero";

export default function AdminEventParticipantsPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const listRef = useRef<AdminEventParticipantsHandle>(null);
  const [event, setEvent] = useState<AdminEvent | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const loadEvent = async () => {
      if (!eventId) {
        setEvent(null);
        return;
      }
      setLoading(true);
      try {
        const data = await fetchAdminEvent(eventId);
        if (active) {
          setEvent(data ?? null);
        }
      } catch (error) {
        console.error("Failed to load event details", error);
        if (active) {
          setEvent(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadEvent();
    return () => {
      active = false;
    };
  }, [eventId]);

  const eventDescription = useMemo(() => {
    if (!event) {
      return "Keep this roster up to date so your festival preparations remain graceful and coordinated.";
    }

    const start = event.startDate ? dayjs(event.startDate) : null;
    const end = event.endDate ? dayjs(event.endDate) : null;
    const window = start
      ? end && end.isAfter(start)
        ? `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`
        : start.format("MMM D, YYYY")
      : undefined;
    const location = event.location?.trim();

    const parts: string[] = [];
    if (window) {
      parts.push(`Scheduled for ${window}`);
    }
    if (location) {
      parts.push(`hosted at ${location}`);
    }

    if (parts.length === 0) {
      return "Keep this roster up to date so your festival preparations remain graceful and coordinated.";
    }

    return `${parts.join(" and ")}. Ensure every devotee who has registered receives a warm welcome.`;
  }, [event]);

  const heroActions: React.ReactNode[] = [
    (
      <Button
        key="back"
        size="md"
        variant="soft"
        color="neutral"
        startDecorator={<ArrowBackRoundedIcon />}
        onClick={() => navigate(-1)}
      >
        Back to events
      </Button>
    ),
    (
      <Button
        key="refresh"
        size="md"
        variant="plain"
        color="neutral"
        startDecorator={<RefreshRoundedIcon />}
        onClick={() => listRef.current?.refresh()}
      >
        Refresh participants
      </Button>
    ),
  ];

  if (eventId) {
    heroActions.splice(1, 0, (
      <Button
        key="details"
        size="md"
        variant="solid"
        color="primary"
        startDecorator={<EventNoteRoundedIcon />}
        component={RouterLink}
        to={`/events/${eventId}`}
      >
        View event details
      </Button>
    ));
  }

  return (
    <Box>
      <PageHero
        eyebrow="Festival seva"
        title={
          event?.title
            ? `Welcoming devotees for ${event.title}`
            : "Cherish every devotee joining this event"
        }
        description={eventDescription}
        actions={heroActions}
      />

      <AdminEventParticipants
        ref={listRef}
        eventId={eventId ?? ""}
        eventTitle={event?.title}
        eventHead={event?.head ?? undefined}
        loadingEvent={loading}
      />
    </Box>
  );
}

